import { ServiceBase } from "@app/shared";
import { NetNewProvider } from "../results/servicing-provider/NetNewProvider";
import { LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType } from "../enumerators";
import { EffectivenessEfficiencyValueEnum } from "./provider-claims/EffectivenessEfficiencyValueEnum";
import { MotiveScoreValueEnum } from "./provider-claims/MotiveScoreValueEnum";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "../network-assessment/pci-dashboard/claims-activity";
import { SortDirections } from "../../shared/waffles";
import { GainsPercentEnum } from "./types";
import { NetNewProviderHistory } from "../results/net-new-provider/netNewProviderHistory";
import { NewPatientClaimsEnum } from "./provider-claims/NewPatientClaimsEnum";

export default class NetNewServicingProviderService extends ServiceBase {
  /**
   * Retrieves the net new providers for a given project and run-configuration
   */
  public async getNetNewServicingProviders(
    projectId: number,
    runConfigurationId: number,
    regionSetId: number,
    specialtyGroupCode: string,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    gainsPercent: GainsPercentEnum,
    targetedType?: string,
    sortedColumn?: string,
    sortDirection?: SortDirections,
    searchValue?: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[]): Promise<NetNewProvider[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !networkAssessmentAnalysisType) {
      return null;
    }

    if (!selectedVolumeLabels) {
      selectedVolumeLabels = [];
    }

    return await this.makePost<NetNewProvider[]>(`netnewservicingproviders/custom-projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/net-new-providers`, {
      gainsPercent,
      networkAssessmentAnalysisType,
      searchValue,
      selectedEffectivenessValues,
      selectedEfficiencyValues,
      selectedLineOfBusiness,
      selectedLocationConfidenceValues,
      selectedMotiveAppropriatenessValues,
      selectedNewPatientClaimsValues,
      selectedVolumeLabels,
      sortDirection,
      sortedColumn,
      specialtyGroupCode,
      targetedType,
    });
  }

  public async updateNetNewIsTargeted(isTargeted: boolean, netNewProviderProjectIds: number[]): Promise<boolean> {
    return await this.makePatch<boolean>(`netnewservicingproviders/target-status/${isTargeted}`, netNewProviderProjectIds);
  }

  public async updateNetNewReasonNameAndDescription(reasonName: string, reasonDescription: string, netNewProviderProjectIds: number[]): Promise<boolean> {
    const body = {
      netNewProviderProjectIds,
      reasonDescription,
      reasonName
    };
    return await this.makePatch<boolean>(`netnewservicingproviders/reason-name-and-description`, body);
  }

  public async getNetNewServicingProviderHistory(netNewProviderProjectId: number): Promise<NetNewProviderHistory> {
    return await this.makeGet<NetNewProviderHistory>(`netnewservicingproviders/${netNewProviderProjectId}/net-new-history`);
  }

  public async getUnprocessedNetNewProviders(projectId: number): Promise<NetNewProvider[]> {
    if (!projectId) {
      return null;
    }

    return await this.makeGet<NetNewProvider[]>(`netnewservicingproviders/${projectId}/unprocessed-net-new-providers`);
  }

  public async regionSetSpecialtyGroupHasNetNewProvidersAsync(
    projectId: number,
    runConfigurationId: number,
    regionSetId: number,
    specialtyGroupCode: string,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<boolean> {

    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !networkAssessmentAnalysisType) {
      return false;
    }

    return await this.makeGet<boolean>(`netnewservicingproviders/custom-projects/${projectId}/run-configurations/${runConfigurationId}/region-sets/${regionSetId}/specialty-groups/${specialtyGroupCode}/network-assessment-analysis-types/${networkAssessmentAnalysisType}/has-net-new`);
  }

  public async projectHasNetNewServicingProviders(
    projectId: number): Promise<boolean> {

    if (!projectId) {
      return false;
    }
    return await this.makeGet<boolean>(`netnewservicingproviders/custom-projects/${projectId}/has-net-new`);
  }
}